import React, { useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { Header } from '../shared/Header';
import { Box, Stack } from '@mui/system';
import { Button, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import axios from 'axios';
import { API_URL } from '../../global/constants';
import SearchIcon from '@mui/icons-material/Search';
import { TextField } from '@mui/material';
import DemoMockinterview from '../Student/mock-interviews/DemoMockinterview';
import { useLocation, useNavigate } from 'react-router';

const AdminMockInterviewDashboard = () => {
	const [interviews, setInterviews] = useState([]);
	const [groupedInterviews, setGroupedInterviews] = useState([]);
	const [expandedGroups, setExpandedGroups] = useState({});
	const [loading, setLoading] = useState(false);
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const { state } = useLocation();
	const navigate = useNavigate();
	const fullName = `${firstName} ${lastName}`;

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const getAllInterviews = async () => {
		setLoading(true);
		try {
			const { data } = await axios.post(`${API_URL}/labs/admin/mock-interviews`, {
				search: searchTerm,
			});
			setGroupedInterviews(data.groupedInterviews);
			if (data.success) {
				console.log(data);
			} else {
				toast.error(data.message);
			}
		} catch (e) {
			console.log(e.message);
			toast.error('Something went wrong, Try Again!!');
		}
		setLoading(false);
	};

	useEffect(() => {
		getAllInterviews();
	}, [searchTerm, state?.refetch]);

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
	};

	const handleToggleExpand = (topicId) => {
		setExpandedGroups((prev) => ({
			...prev,
			[topicId]: !prev[topicId],
		}));
	};

	// Filtered Interviews
	const filteredGroupedInterviews = groupedInterviews
		.map((group) => {
			const filteredInterviews = group.mockInterviews.filter((interview) =>
				interview.job_role.toLowerCase().includes(searchTerm.toLowerCase())
			);

			if (filteredInterviews.length > 0) {
				return {
					...group,
					mockInterviews: filteredInterviews,
				};
			}
			return null;
		})
		.filter((group) => group !== null);

	return (
		<>
			<Header />
			<Stack
				spacing={{ sm: 5, xs: 3, lg: 5 }}
				style={{
					backgroundColor: '#ecf7fa',
					width: '100%',
					minHeight: '100%',
					padding: '2vw 0vw 2vw 4vw',
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: isMobile ? 'column' : 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						width: '90vw',
						marginRight: '25px',
					}}
				>
					<Typography
						fontWeight={'bold'}
						fontSize={isMobile ? '1.1rem' : '1.3rem'}
						style={{ color: '#495057' }}
					>
						All Mock-Interviews
					</Typography>
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'center',
							alignItems: 'center',
							gap: '15px',
							width: '55vw',
							marginTop: isMobile ? '10px' : '',
						}}
					>
						<TextField
							variant="standard"
							label="Search..."
							value={searchTerm}
							onChange={handleSearchChange}
							fullWidth={isMobile}
							InputProps={{
								endAdornment: (
									<SearchIcon style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
								),
							}}
							style={{ marginBottom: '10px' }}
						/>
						<Button
							onClick={() => navigate('/mock-interviews/all-reports')}
							variant="outlined"
							size="medium"
						>
							<Typography fontWeight="semibold" style={{ color: '#224C84' }}>
								Interview Reports
							</Typography>
						</Button>
						<Button
							onClick={() => navigate('/create-mock-interviews')}
							variant="outlined"
							size="medium"
						>
							<Typography fontWeight="semibold" style={{ color: '#224C84' }}>
								Create Interview
							</Typography>
						</Button>
					</div>
				</div>
				<Stack
					direction={isMobile ? 'column' : 'row'}
					useFlexGap
					flexWrap={'wrap'}
					spacing={4}
					alignItems={'center'}
					margin={10}
					justifyContent={'start'}
				>
					{loading && (
						<Stack spacing={5} justifyContent={'start'} direction={'row'}>
							{Array(4)
								.fill(null)
								.map((_, index) => (
									<Stack
										key={index}
										p={{ sm: 3, xs: 2 }}
										bgcolor={'#fff'}
										spacing={2}
										width={{ sm: '400px', xs: '100%' }}
										height={'auto'}
										borderRadius={'20px'}
										boxShadow={'8px 6px 12px 0 rgba(13,31,61,.04)'}
									>
										<Skeleton variant="text" width={'250px'} />
										<Skeleton variant="text" width={'200px'} />
										<Skeleton variant="text" />
										<Skeleton variant="button" width={'100px'} />
									</Stack>
								))}
						</Stack>
					)}

					{!loading && filteredGroupedInterviews?.length === 0 && (
						<Typography>No interviews available for you.</Typography>
					)}

					{!loading &&
						filteredGroupedInterviews?.map((group) => (
							<Box
								key={group.topicId}
								sx={{
									display: 'block',
									width: '90vw',
									mb: 4,
									justifyContent: 'start',
								}}
							>
								<Typography
									variant="h6"
									fontWeight="bold"
									style={{ color: '#495057' }}
								>
									{group.topicName || 'No Topic'}
								</Typography>
								<Stack
									direction={isMobile ? 'column' : 'row'}
									sx={{
										display: 'flex',
										flexDirection: isMobile ? 'column' : 'row',
										flexWrap: 'wrap',
										justifyContent: isMobile ? 'center' : 'start',
										mt: isMobile ? 2 : 2,
										width: '90vw',
										gap: '2.5rem',
									}}
								>
									{(expandedGroups[group.topicId]
										? group.mockInterviews
										: group.mockInterviews.slice(0, 4)
									).map((interview) => (
										<DemoMockinterview
											interviewDescription={interview.job_description}
											key={interview._id}
											interview_id={interview._id}
											pilot_id={interview.pilot_id}
											role={interview.job_role}
											poster={interview.job_poster}
											interview_level={interview.interview_level}
											questions={interview.questions}
											fullName={fullName}
											skills={interview.skills}
											refetch={getAllInterviews}
										/>
									))}
								</Stack>
								{group.mockInterviews.length > 4 && (
									<Button
										onClick={() => handleToggleExpand(group.topicId)}
										variant="outlined"
										style={{ marginTop: '16px' }}
									>
										{expandedGroups[group.topicId] ? 'Show Fewer' : 'Show More'}
									</Button>
								)}
							</Box>
						))}
				</Stack>
			</Stack>
		</>
	);
};

export default AdminMockInterviewDashboard;
